// src/utils/mergeRefs.ts
/**
* Attach a node to one ref, and hand back the way to detach it.
*
* - A callback ref that returns its own cleanup gets that cleanup back; React 19 owns the
*   contract, and a hook using it never sees the legacy `null` call.
* - One that returns nothing is detached the legacy way instead, with a `null` call.
* - An object ref's `current` is cleared.
*/
const attachRef = (ref, node) => {
	if (typeof ref === "function") {
		const cleanup = ref(node);
		return typeof cleanup === "function" ? cleanup : () => ref(null);
	}
	if (ref == null) return () => {};
	ref.current = node;
	return () => {
		ref.current = null;
	};
};
/**
* Compose several refs into the one a component can hand to an element.
*
* - Accepts callback refs and ref objects, in any mix.
* - Returns a cleanup of its own, so a callback ref's cleanup survives the merge. Dropping it
*   would leave a hook that detaches through its cleanup — `useElement` does — holding a node
*   that is no longer in the document.
*/
const mergeRefs = (...refs) => {
	return (node) => {
		const cleanups = refs.map((ref) => attachRef(ref, node));
		return () => cleanups.forEach((cleanup) => cleanup());
	};
};
export { mergeRefs };

//# sourceMappingURL=mergeRefs.js.map